import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, Wrench, Send, CheckCircle2, AlertTriangle, Link2,
  Clock, User, Calendar, MapPin, Hash, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  statusEquipLabel, STATUS_EQUIP_BADGE,
  propriedadeLabel, tipoClienteLabel,
} from "../lib/labels";
import { ImageUploader } from "../components/ImageUploader";

const MODALIDADES = [
  { v: "locacao_nova", l: "LOCAÇÃO NOVA" },
  { v: "troca", l: "TROCA" },
  { v: "aditivo", l: "ADITIVO" },
  { v: "devolucao", l: "DEVOLUÇÃO" },
  { v: "oficina", l: "OFICINA" },
  { v: "estoque", l: "ESTOQUE" },
];

const STATUS_TARGETS = [
  { v: "com_cliente", l: "COM CLIENTE" },
  { v: "disponivel", l: "DISPONÍVEL" },
  { v: "reservado", l: "RESERVADO" },
  { v: "oficina", l: "NA OFICINA" },
  { v: "em_rota", l: "EM ROTA" },
  { v: "devolvido", l: "DEVOLVIDO" },
  { v: "extraviado", l: "EXTRAVIADO" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
}

function ModalVincular({ open, onClose, equipamento, onSaved }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [cli, setCli] = useState(null);
  const [modalidade, setModalidade] = useState("locacao_nova");
  const [contratoId, setContratoId] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setOpts([]); return; }
    const t = setTimeout(() => {
      api.get("/inventario/clientes", { params: { q, limit: 8 } })
        .then((r) => setOpts(r.data.items || []));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const salvar = async () => {
    if (!cli) return toast.error("Selecione um cliente");
    setSaving(true);
    try {
      const { data } = await api.post(`/inventario/equipamentos/${equipamento.id}/vincular-cliente`, {
        cliente_id: cli.id,
        modalidade,
        contrato_id: contratoId || null,
        observacoes: obs || null,
      });
      toast.success(`Vinculado a ${cli.nome_fantasia}`);
      onSaved(data.equipamento);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao vincular");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="modal-vincular">
        <DialogHeader>
          <DialogTitle>Vincular a cliente</DialogTitle>
          <DialogDescription>Equipamento {equipamento?.serial}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide">Cliente</Label>
            {cli ? (
              <div className="flex items-center justify-between p-2 border border-zinc-200 rounded mt-1 bg-zinc-50">
                <div className="text-sm">
                  <div className="font-medium">{cli.nome_fantasia}</div>
                  <div className="text-xs text-zinc-500">{cli.cnpj || "—"} · {cli.cidade}/{cli.uf}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setCli(null)}>Trocar</Button>
              </div>
            ) : (
              <>
                <Input
                  placeholder="Buscar cliente por nome ou CNPJ..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="mt-1"
                  data-testid="vincular-cliente-search"
                />
                {opts.length > 0 && (
                  <div className="border rounded mt-1 max-h-48 overflow-auto">
                    {opts.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => setCli(o)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 border-b last:border-0"
                        data-testid={`vincular-opt-${o.id}`}
                      >
                        <div className="font-medium">{o.nome_fantasia}</div>
                        <div className="text-xs text-zinc-500">{o.cnpj || "—"} · {o.cidade}/{o.uf}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Modalidade</Label>
            <Select value={modalidade} onValueChange={setModalidade}>
              <SelectTrigger data-testid="vincular-modalidade"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODALIDADES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Contrato (opcional)</Label>
            <Input value={contratoId} onChange={(e) => setContratoId(e.target.value)} placeholder="ID/número do contrato" data-testid="vincular-contrato" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Observações</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} data-testid="vincular-obs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={!cli || saving} data-testid="vincular-confirmar">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModalStatus({ open, onClose, equipamento, onSaved }) {
  const [novoStatus, setNovoStatus] = useState("disponivel");
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);
  const desvincula = ["disponivel", "oficina", "devolvido"].includes(novoStatus);

  const salvar = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/inventario/equipamentos/${equipamento.id}/status`, {
        novo_status: novoStatus,
        motivo: motivo || null,
        observacoes: obs || null,
        desvincular_cliente: desvincula,
      });
      toast.success(`Status: ${statusEquipLabel(novoStatus)}`);
      onSaved(data.equipamento);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alterar status");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="modal-status">
        <DialogHeader>
          <DialogTitle>Alterar status</DialogTitle>
          <DialogDescription>
            Equipamento {equipamento?.serial} · atual: <strong>{statusEquipLabel(equipamento?.status)}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide">Novo status</Label>
            <Select value={novoStatus} onValueChange={setNovoStatus}>
              <SelectTrigger data-testid="status-novo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_TARGETS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
            {desvincula && equipamento?.cliente_id && (
              <p className="text-[11px] text-amber-700 mt-1">⚠ Isso desvincula do cliente atual.</p>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Motivo</Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex: defeito_no_motor" data-testid="status-motivo" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Observações</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} data-testid="status-obs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} data-testid="status-confirmar">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EquipamentoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [vincularOpen, setVincularOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const carregar = async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get(`/inventario/equipamentos/${id}/detalhe`),
        api.get(`/inventario/equipamentos/${id}/historico`),
      ]);
      setData(d.data);
      setHistorico(h.data.items || []);
    } catch (e) {
      toast.error("Equipamento não encontrado");
      navigate("/app/inventario");
    } finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, [id]);

  if (loading || !data) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }

  const eq = data.equipamento;
  const cli = data.cliente_atual;

  const acaoOficina = async () => {
    try {
      const { data: r } = await api.patch(`/inventario/equipamentos/${id}/status`, {
        novo_status: "oficina", motivo: "envio_oficina", desvincular_cliente: true,
      });
      toast.success("Enviado para oficina");
      setData({ ...data, equipamento: r.equipamento, cliente_atual: null });
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const acaoDisponivel = async () => {
    try {
      const { data: r } = await api.patch(`/inventario/equipamentos/${id}/status`, {
        novo_status: "disponivel", motivo: "marcado_disponivel", desvincular_cliente: true,
      });
      toast.success("Marcado como disponível");
      setData({ ...data, equipamento: r.equipamento, cliente_atual: null });
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const acaoExtraviado = async () => {
    if (!window.confirm("⚠ Marcar como EXTRAVIADO é uma ação imutável. Confirma?")) return;
    try {
      const { data: r } = await api.patch(`/inventario/equipamentos/${id}/status`, {
        novo_status: "extraviado", motivo: "perda_confirmada", desvincular_cliente: true,
      });
      toast.success("Marcado como extraviado");
      setData({ ...data, equipamento: r.equipamento, cliente_atual: null });
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const imutavel = ["extraviado", "baixado"].includes(eq.status);

  return (
    <div className="space-y-5" data-testid="page-equip-detalhe">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/inventario")} data-testid="back-inventario">
          <ArrowLeft className="w-4 h-4 mr-1" /> Inventário
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-zinc-900 font-mono">{eq.serial}</h1>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
            <span>Placa: <strong>{eq.placa || "—"}</strong></span>
            <span>·</span>
            <span>{propriedadeLabel(eq.propriedade)}</span>
            <Badge className={`${STATUS_EQUIP_BADGE[eq.status]} font-mono text-[10px] ml-2`}>
              {statusEquipLabel(eq.status)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setVincularOpen(true)} disabled={imutavel} data-testid="btn-vincular">
          <Link2 className="w-4 h-4 mr-1" /> Vincular cliente
        </Button>
        <Button size="sm" variant="outline" onClick={() => setStatusOpen(true)} disabled={imutavel} data-testid="btn-alterar-status">
          <CheckCircle2 className="w-4 h-4 mr-1" /> Alterar status
        </Button>
        <Button size="sm" variant="outline" onClick={acaoOficina} disabled={imutavel} data-testid="btn-oficina">
          <Wrench className="w-4 h-4 mr-1" /> Enviar p/ oficina
        </Button>
        <Button size="sm" variant="outline" onClick={acaoDisponivel} disabled={imutavel} data-testid="btn-disponivel">
          <Send className="w-4 h-4 mr-1" /> Marcar disponível
        </Button>
        <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={acaoExtraviado} disabled={imutavel} data-testid="btn-extraviado">
          <AlertTriangle className="w-4 h-4 mr-1" /> Extraviado
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Dados */}
        <Card className="p-5 lg:col-span-2 space-y-3">
          <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Dados do equipamento</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info icon={Hash} label="Serial" value={eq.serial} mono />
            <Info icon={Hash} label="Placa" value={eq.placa} mono />
            <Info icon={Calendar} label="Cadastrado em" value={eq.cadastrado_em} />
            <Info icon={Calendar} label="Início vínculo atual" value={eq.data_inicio} />
            <Info icon={Hash} label="Modalidade" value={eq.modalidade ? String(eq.modalidade).toUpperCase() : "—"} />
            <Info icon={Hash} label="Contrato" value={eq.contrato_id} />
          </div>
          {eq.observacoes && (
            <div className="text-xs text-zinc-600 bg-zinc-50 p-3 rounded">
              <span className="text-zinc-400 uppercase tracking-wide text-[10px]">Observações</span><br />
              {eq.observacoes}
            </div>
          )}
          <div className="pt-3 border-t">
            <ImageUploader
              photos={eq.fotos || []}
              modulo="equipamento"
              refId={eq.id}
              label="Fotos do equipamento"
              onChange={(fotos) => setData({ ...data, equipamento: { ...eq, fotos } })}
            />
          </div>
        </Card>

        {/* Cliente atual */}
        <Card className="p-5 space-y-3" data-testid="cliente-atual-card">
          <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Cliente atual</h3>
          {cli ? (
            <div className="space-y-2">
              <Link to={`/app/inventario/cliente/${cli.id}`} className="font-semibold text-blue-700 hover:underline text-sm block">
                {cli.nome_fantasia}
              </Link>
              <div className="text-xs text-zinc-500 space-y-1">
                <div className="font-mono">{cli.cnpj || "—"}</div>
                <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {cli.cidade || "—"} / {cli.uf || "—"}</div>
                <Badge className="bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[10px]">
                  {tipoClienteLabel(cli.tipo_cliente)}
                </Badge>
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-400 py-4 text-center bg-zinc-50 rounded">
              <Hash className="w-5 h-5 mx-auto mb-1 text-zinc-300" />
              Área neutra · sem cliente vinculado
            </div>
          )}
        </Card>
      </div>

      {/* Histórico timeline */}
      <Card className="p-5">
        <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-4">
          Histórico de movimentações ({historico.length})
        </h3>
        {historico.length === 0 ? (
          <div className="text-center text-sm text-zinc-400 py-6">Nenhuma movimentação registrada.</div>
        ) : (
          <div className="space-y-3" data-testid="historico-timeline">
            {historico.map((m, idx) => (
              <div key={m.id} className="flex gap-3" data-testid={`mov-${idx}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-2.5 h-2.5 rounded-full ${STATUS_EQUIP_BADGE[m.status_novo] || "bg-zinc-200"}`} />
                  {idx < historico.length - 1 && <div className="w-px flex-1 bg-zinc-200 mt-1" />}
                </div>
                <div className="flex-1 pb-3 -mt-0.5">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge className={`${STATUS_EQUIP_BADGE[m.status_anterior] || "bg-zinc-100"} font-mono text-[10px]`}>
                      {statusEquipLabel(m.status_anterior)}
                    </Badge>
                    <span className="text-zinc-400">→</span>
                    <Badge className={`${STATUS_EQUIP_BADGE[m.status_novo] || "bg-zinc-100"} font-mono text-[10px]`}>
                      {statusEquipLabel(m.status_novo)}
                    </Badge>
                    {m.modalidade && <span className="text-zinc-500">· {String(m.modalidade).toUpperCase()}</span>}
                    {m.motivo && <span className="text-zinc-500">· {m.motivo}</span>}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    {m.cliente_origem_nome && <>De <strong>{m.cliente_origem_nome}</strong> </>}
                    {m.cliente_destino_nome && <>para <strong>{m.cliente_destino_nome}</strong></>}
                    {!m.cliente_origem_nome && !m.cliente_destino_nome && "Sem mudança de cliente"}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(m.data_movimentacao)}</span>
                    {m.usuario_responsavel?.email && (
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{m.usuario_responsavel.email}</span>
                    )}
                    {m.contrato_id && <span>· contrato {m.contrato_id}</span>}
                  </div>
                  {m.observacoes && <div className="text-xs text-zinc-500 mt-1 italic">"{m.observacoes}"</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ModalVincular open={vincularOpen} onClose={() => setVincularOpen(false)} equipamento={eq} onSaved={() => carregar()} />
      <ModalStatus open={statusOpen} onClose={() => setStatusOpen(false)} equipamento={eq} onSaved={() => carregar()} />
    </div>
  );
}

function Info({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-zinc-400 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</div>
        <div className={`text-sm text-zinc-800 truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
      </div>
    </div>
  );
}
